import {
  isRecord,
  nullableString,
  optionalString,
  optionalStringArray,
  request,
  requestNoContent,
  requireBoolean,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'
import type { Replace } from '@/lib/type-utils'

type Schemas = components['schemas']

export type Impersonation = Schemas['ImpersonationState']
export type SsoConfig = Schemas['SsoConfigResponse']
export type SsoConfigInput = Schemas['PutSsoConfigRequest']
export type FederatedRoleMapping = Schemas['FederatedRoleMappingConfig']
export type FederatedRoleMappingInput = Schemas['FederatedRoleMappingInput']

export type Me = Replace<Schemas['MeResponse'], 'entityId' | 'permissions' | 'impersonation', {
  entityId?: string
  permissions?: string[]
  impersonation?: Impersonation
}>

function decodeImpersonation(raw: unknown): Impersonation | undefined {
  if (raw === undefined || raw === null) return undefined
  if (!isRecord(raw)) throw new Error('Malformed impersonation')
  return {
    actorUserId: requireString(raw.actorUserId, 'impersonation.actorUserId'),
    actorName: requireString(raw.actorName, 'impersonation.actorName'),
    actorEmail: nullableString(raw.actorEmail, 'impersonation.actorEmail'),
  } satisfies Impersonation
}

function decodeMe(raw: unknown): Me {
  if (!isRecord(raw)) throw new Error('Malformed me response')
  return {
    userId: requireString(raw.userId, 'userId'),
    email: nullableString(raw.email, 'email'),
    name: requireString(raw.name, 'name'),
    entityId: optionalString(raw.entityId, 'entityId'),
    permissions: optionalStringArray(raw.permissions, 'permissions'),
    impersonation: decodeImpersonation(raw.impersonation),
  } satisfies Me
}

function decodeSsoConfig(raw: unknown): SsoConfig {
  if (!isRecord(raw)) throw new Error('Malformed SSO configuration')
  const scopes = optionalStringArray(raw.scopes, 'scopes') ?? null
  const enabled = raw.enabled === null || raw.enabled === undefined
    ? null
    : requireBoolean(raw.enabled, 'enabled')
  return {
    configured: requireBoolean(raw.configured, 'configured'),
    zitadelOrgId: raw.zitadelOrgId === null || raw.zitadelOrgId === undefined
      ? null
      : requireString(raw.zitadelOrgId, 'zitadelOrgId'),
    idpId: raw.idpId === null || raw.idpId === undefined ? null : requireString(raw.idpId, 'idpId'),
    name: raw.name === null || raw.name === undefined ? null : requireString(raw.name, 'name'),
    issuer: raw.issuer === null || raw.issuer === undefined ? null : requireString(raw.issuer, 'issuer'),
    clientId: raw.clientId === null || raw.clientId === undefined ? null : requireString(raw.clientId, 'clientId'),
    scopes,
    enabled,
  } satisfies SsoConfig
}

function decodeFederatedRoleMapping(raw: unknown): FederatedRoleMapping {
  if (!isRecord(raw) || !Array.isArray(raw.targets))
    throw new Error('Malformed federated role mapping')
  return {
    mappingId: requireString(raw.mappingId, 'mappingId'),
    upstreamRole: requireString(raw.upstreamRole, 'upstreamRole'),
    targets: raw.targets.map((target) => {
      if (!isRecord(target)) throw new Error('Malformed federated role target')
      return {
        targetType: requireString(target.targetType, 'targetType'),
        targetId: requireString(target.targetId, 'targetId'),
        entityId: target.entityId === null || target.entityId === undefined
          ? null
          : requireString(target.entityId, 'entityId'),
        targetName: requireString(target.targetName, 'targetName'),
        entityName: target.entityName === null || target.entityName === undefined
          ? null
          : requireString(target.entityName, 'entityName'),
      }
    }),
  } satisfies FederatedRoleMapping
}

export const identityApi = {
  me: (entityId?: string) => {
    const query = new URLSearchParams()
    if (entityId) query.set('entity_id', entityId)
    const qs = query.toString()
    return request(`/api/v1/me${qs ? `?${qs}` : ''}`, decodeMe)
  },
  getSsoConfig: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/sso-config`, decodeSsoConfig),
  saveSsoConfig: (entityId: string, input: SsoConfigInput) =>
    requestNoContent(`/api/v1/entities/${entityId}/sso-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),
  getFederatedRoleMappings: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/sso-config/role-mappings`, (raw) => {
      if (!Array.isArray(raw)) throw new Error('Malformed federated role mappings')
      return raw.map(decodeFederatedRoleMapping)
    }),
  saveFederatedRoleMappings: (
    entityId: string,
    mappings: FederatedRoleMappingInput[],
  ) => requestNoContent(`/api/v1/entities/${entityId}/sso-config/role-mappings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mappings } satisfies Schemas['PutFederatedRoleMappingsRequest']),
  }),
}
